import { createContext, useContext, useEffect, useRef, useState, useCallback } from "react";
import { X, Download, ExternalLink, AlertTriangle, Settings, Check } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { getProgressFor, saveProgress } from "@/lib/store";
import { Button } from "@/components/ui/button";

const PlayerContext = createContext(null);
export const usePlayer = () => useContext(PlayerContext);

const trackLabel = (t, i, kind) =>
  t.label || (t.language ? t.language.toUpperCase() : `${kind} ${i + 1}`);

export const PlayerProvider = ({ children }) => {
  const { creds } = useAuth();
  const [current, setCurrent] = useState(null); // { url, title, poster, meta }
  const [failed, setFailed] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [tracks, setTracks] = useState({ audio: [], text: [] });
  const [activeAudio, setActiveAudio] = useState(0);
  const [activeText, setActiveText] = useState(-1);
  const videoRef = useRef(null);
  const lastSaveRef = useRef(0);

  const openPlayer = useCallback((opts) => {
    setFailed(false);
    setShowSettings(false);
    setTracks({ audio: [], text: [] });
    setActiveText(-1);
    setActiveAudio(0);
    setCurrent(opts);
  }, []);

  const close = useCallback(() => {
    const v = videoRef.current;
    if (v && current?.meta && v.duration) {
      saveProgress(creds, { ...current.meta, position: v.currentTime, duration: v.duration });
    }
    setCurrent(null);
    setShowSettings(false);
  }, [creds, current]);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && current && close();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [current, close]);

  const onLoaded = () => {
    const v = videoRef.current;
    if (!v) return;
    if (current?.meta) {
      const saved = getProgressFor(creds, current.meta.kind, current.meta.playableId);
      if (saved?.position && saved.position < (v.duration || Infinity) - 5) {
        v.currentTime = saved.position;
      }
    }
    // Discover audio & subtitle tracks when the browser exposes them
    const audio = v.audioTracks ? Array.from(v.audioTracks) : [];
    const text = v.textTracks ? Array.from(v.textTracks) : [];
    text.forEach((t) => {
      if (t.mode === "showing") t.mode = "disabled"; // start with subs off
    });
    setTracks({ audio, text });
    const ai = audio.findIndex((t) => t.enabled);
    setActiveAudio(ai < 0 ? 0 : ai);
    setActiveText(-1);
  };

  const selectAudio = (i) => {
    const v = videoRef.current;
    if (!v?.audioTracks) return;
    Array.from(v.audioTracks).forEach((t, idx) => {
      t.enabled = idx === i;
    });
    setActiveAudio(i);
  };

  const selectText = (i) => {
    const v = videoRef.current;
    if (!v?.textTracks) return;
    Array.from(v.textTracks).forEach((t, idx) => {
      t.mode = idx === i ? "showing" : "disabled";
    });
    setActiveText(i);
  };

  const hasTrackMenu = tracks.audio.length > 1 || tracks.text.length > 0;

  const onTimeUpdate = () => {
    const v = videoRef.current;
    if (!v || !current?.meta || !v.duration) return;
    const now = Date.now();
    if (now - lastSaveRef.current > 4000) {
      lastSaveRef.current = now;
      saveProgress(creds, { ...current.meta, position: v.currentTime, duration: v.duration });
    }
  };

  return (
    <PlayerContext.Provider value={{ openPlayer }}>
      {children}
      {current && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/90 backdrop-blur-md p-0 sm:p-6">
          <div
            data-testid="video-player-container"
            className="relative w-full h-full sm:h-auto sm:max-w-5xl sm:rounded-2xl overflow-hidden bg-black border border-white/10"
          >
            <div className="absolute top-0 inset-x-0 z-10 flex items-center justify-between p-4 bg-gradient-to-b from-black/80 to-transparent">
              <p className="text-white font-semibold text-sm sm:text-base line-clamp-1 pr-4 font-heading">
                {current.title}
              </p>
              <div className="flex items-center gap-2 shrink-0">
                {hasTrackMenu && (
                  <div className="relative">
                    <button
                      data-testid="player-settings-button"
                      onClick={() => setShowSettings((s) => !s)}
                      className="h-9 w-9 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white"
                    >
                      <Settings className="h-5 w-5" />
                    </button>
                    {showSettings && (
                      <div
                        data-testid="player-settings-menu"
                        className="absolute right-0 mt-2 w-60 rounded-xl bg-[#0C1017] border border-white/10 shadow-2xl p-2 text-sm"
                      >
                        {tracks.audio.length > 1 && (
                          <div className="mb-2">
                            <p className="px-2 py-1 text-[11px] uppercase tracking-widest text-slate-500">Audio</p>
                            {tracks.audio.map((t, i) => (
                              <MenuRow key={`a${i}`} active={activeAudio === i} onClick={() => selectAudio(i)} label={trackLabel(t, i, "Audio")} />
                            ))}
                          </div>
                        )}
                        {tracks.text.length > 0 && (
                          <div>
                            <p className="px-2 py-1 text-[11px] uppercase tracking-widest text-slate-500">Subtitles</p>
                            <MenuRow active={activeText === -1} onClick={() => selectText(-1)} label="Off" />
                            {tracks.text.map((t, i) => (
                              <MenuRow key={`t${i}`} active={activeText === i} onClick={() => selectText(i)} label={trackLabel(t, i, "Subtitle")} />
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
                <button
                  data-testid="player-close-button"
                  onClick={close}
                  className="h-9 w-9 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>

            {!failed ? (
              <video
                ref={videoRef}
                src={current.url}
                poster={current.poster || undefined}
                controls
                autoPlay
                playsInline
                onLoadedMetadata={onLoaded}
                onTimeUpdate={onTimeUpdate}
                onError={() => setFailed(true)}
                className="w-full h-full sm:h-auto sm:max-h-[80vh] bg-black object-contain"
              />
            ) : (
              <div className="flex flex-col items-center justify-center text-center gap-4 py-24 px-6 min-h-[50vh]">
                <div className="h-14 w-14 rounded-2xl bg-amber-500/10 flex items-center justify-center">
                  <AlertTriangle className="h-7 w-7 text-amber-400" />
                </div>
                <div>
                  <p className="text-white font-semibold">This title can't play in the browser</p>
                  <p className="text-slate-400 text-sm max-w-md mt-1">
                    Your panel may use a format (e.g. MKV) or plain HTTP that browsers block for
                    in-app playback. Open it directly or download it instead.
                  </p>
                </div>
                <div className="flex flex-wrap gap-3 justify-center">
                  <a href={current.url} target="_blank" rel="noopener noreferrer">
                    <Button className="bg-[#14A3FF] hover:bg-[#0E8FE6] text-white">
                      <ExternalLink className="h-4 w-4 mr-2" /> Open in new tab
                    </Button>
                  </a>
                  <a href={current.url} target="_blank" rel="noopener noreferrer">
                    <Button variant="outline" className="border-white/15 bg-white/5 text-white hover:bg-white/10">
                      <Download className="h-4 w-4 mr-2" /> Download
                    </Button>
                  </a>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </PlayerContext.Provider>
  );
};

const MenuRow = ({ active, onClick, label }) => (
  <button
    onClick={onClick}
    data-testid="player-track-option"
    className={`w-full flex items-center justify-between gap-2 px-2 py-2 rounded-lg text-left transition-colors ${
      active ? "bg-white/10 text-white" : "text-slate-300 hover:bg-white/5"
    }`}
  >
    <span className="line-clamp-1">{label}</span>
    {active ? <Check className="h-4 w-4 text-[#14A3FF] shrink-0" /> : null}
  </button>
);
